// RITA-1525 — Darstellung der Inline-Knoten eines Blocks (Text, Platzhalter, Kästchen, Zeilenumbruch).
//
// Dieselbe Darstellung dient Seitenansicht, Messung, Druck und Bearbeitung: Jeder Knoten trägt
// `data-knoten` (Index im Modell), ein Textlauf enthält genau einen Textknoten, Platzhalter und
// Kästchen sind `contenteditable="false"`-Inseln (unteilbar, der Caret steht davor oder dahinter).
// Ein leerer Inhalt und ein Inhalt, der mit einem Zeilenumbruch, Platzhalter oder Kästchen endet,
// bekommt ein abschließendes <br>, damit Chromium die (leere) Zeile zeigt und der Caret hinter der
// Insel zuverlässig stehen kann. Ein formatierter Platzhalter (RITA-1533) trägt dieselbe Hülle wie ein
// Textlauf INNERHALB seiner Insel; ein leerer Wert bleibt ohne Hülle, sonst greift die Markierung
// fehlender Werte (`:empty` in vertragseditor.css) nicht mehr.
import React from 'react';
import { platzhalterwert, Wertemodus } from '../model/platzhalterwerte';
import { Inlineknoten, Zeichenformat } from '../model/types';

interface Props {
  inhalt: Inlineknoten[];
  modus: Wertemodus;
}

function mitFormat(inhalt: string, format: Zeichenformat): React.ReactNode {
  if (inhalt === '') return inhalt;
  let knoten: React.ReactNode = inhalt;
  if (format.unterstrichen) knoten = <u>{knoten}</u>;
  if (format.kursiv) knoten = <i>{knoten}</i>;
  if (format.fett) knoten = <b>{knoten}</b>;
  return knoten;
}

/**
 * Eigene Schriftgröße (RITA-1534) als Stil am Knoten selbst – Lauf-Span bzw. Platzhalter-Insel –, damit die
 * Struktur für die Auswahlzuordnung (`editor/domAuswahl.ts`) gleich bleibt. Ohne eigene Größe erbt der Knoten
 * die Grundgröße der Seite bzw. die feste Größe der Fußzeile; die einheitenlose Zeilenhöhe wächst mit.
 */
function groessenStil(format: Zeichenformat): React.CSSProperties | undefined {
  return format.groessePt === undefined ? undefined : { fontSize: `${format.groessePt}pt` };
}

function knotenAnsicht(knoten: Inlineknoten, index: number, modus: Wertemodus): React.ReactNode {
  switch (knoten.typ) {
    case 'text':
      return (
        <span className="vertrag-lauf" data-knoten={index} style={groessenStil(knoten)}>
          {mitFormat(knoten.text, knoten)}
        </span>
      );
    case 'platzhalter': {
      const wert = platzhalterwert(knoten.schluessel, modus);
      if (wert === null) {
        return (
          <span
            className="vertrag-platzhalter"
            data-platzhalter={knoten.schluessel}
            data-knoten={index}
            contentEditable={false}
            style={groessenStil(knoten)}
          >
            {mitFormat(`{{${knoten.schluessel}}}`, knoten)}
          </span>
        );
      }
      return (
        <span
          className="vertrag-platzhalter-wert"
          data-platzhalter={knoten.schluessel}
          data-wert=""
          data-knoten={index}
          contentEditable={false}
          style={groessenStil(knoten)}
        >
          {mitFormat(wert, knoten)}
        </span>
      );
    }
    case 'kaestchen':
      return (
        <span
          className="vertrag-kaestchen"
          data-angekreuzt={knoten.angekreuzt ? 'true' : 'false'}
          data-knoten={index}
          contentEditable={false}
          role="img"
          aria-label={knoten.angekreuzt ? 'Kästchen angekreuzt' : 'Kästchen leer'}
        />
      );
    case 'zeilenumbruch':
      return <br data-knoten={index} />;
    default:
      return null;
  }
}

function InlineAnsicht({ inhalt, modus }: Props) {
  const letzter = inhalt[inhalt.length - 1];
  const abschluss = !letzter || letzter.typ !== 'text';
  return (
    <>
      {inhalt.map((knoten, index) => (
        // Inline-Knoten haben keine eigene Kennung und werden in der Anzeige nie umsortiert.
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${knoten.typ}-${index}`}>{knotenAnsicht(knoten, index, modus)}</React.Fragment>
      ))}
      {abschluss && <br data-abschluss="" />}
    </>
  );
}

export default InlineAnsicht;
